import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { TourStep, TourStatus } from './types';

interface TourState {
  status: TourStatus;
  steps: TourStep[];
  stepIndex: number;
  isOpen: boolean;
}

interface TourActions {
  openWelcome: (steps: TourStep[]) => void;
  start: () => void;
  close: () => void;
  next: () => void;
  back: () => void;
  finish: () => void;
  setStepIndex: (idx: number) => void;
}

export type TourContextValue = TourState & TourActions;

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<TourStatus>('idle');
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [stepIndex, setStepIndex] = useState(0);

  const isOpen = status === 'welcome' || status === 'running';

  const openWelcome = useCallback((nextSteps: TourStep[]) => {
    setSteps(nextSteps);
    setStepIndex(0);
    setStatus('welcome');
  }, []);

  const start = useCallback(() => {
    setStatus('running');
  }, []);

  const close = useCallback(() => {
    setStatus('idle');
    setSteps([]);
    setStepIndex(0);
  }, []);

  const finish = useCallback(() => {
    setStatus('completed');
  }, []);

  const next = useCallback(() => {
    setStepIndex((idx) => Math.min(idx + 1, Math.max(steps.length - 1, 0)));
  }, [steps.length]);

  const back = useCallback(() => {
    setStepIndex((idx) => Math.max(idx - 1, 0));
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({
      status,
      steps,
      stepIndex,
      isOpen,
      openWelcome,
      start,
      close,
      next,
      back,
      finish,
      setStepIndex,
    }),
    [status, steps, stepIndex, isOpen, openWelcome, start, close, next, back, finish],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTourGuide(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTourGuide must be used within TourProvider');
  return ctx;
}

